import type { Session } from "@ory/client"
import dayjs from "dayjs"
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import type { ReactNode } from "react"

import APIConfig from "@/services/config"
import { orySDK } from "@/services/ory"
import type { UserMetadataPublic } from "@/services/types"

export interface AuthContext {
  checkSession: () => Promise<Session | undefined>
  isAuthenticated: boolean
  logout: () => Promise<void>
  metadataPublic: UserMetadataPublic
  session: Session | undefined
}

export const AuthContext = createContext<AuthContext | undefined>(undefined)
const STORAGE_KEY = "session"
// Resolved per call rather than captured at module scope. `const STORAGE =
// localStorage` binds whatever the global is at import time, which under a test
// runner is before the DOM environment has installed its own implementation --
// so it would bind undefined and fail at the first use, far from the cause.
const storage = () => localStorage
const DEFAULT_METADATA = { role: "", org_id: "" }

const gotoLogin = () =>
  window.location.replace(`${APIConfig.kratosApi}/self-service/login/browser`)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | undefined>(undefined)
  const [metadataPublic, setMetadataPublic] =
    useState<UserMetadataPublic>(DEFAULT_METADATA)

  useEffect(() => {
    // Check session on initial load and handle redirection if needed
    const validateSession = async () => {
      if (window.location.pathname !== "/login") {
        await checkSession()
      }
    }

    validateSession()
  }, [])

  const logout = useCallback(async () => {
    try {
      // Invalidate the session using Ory SDK
      const { data: flow } = await orySDK.createBrowserLogoutFlow()
      // Use the received token to "update" the flow and thus perform the logout
      await orySDK.updateLogoutFlow({
        token: flow.logout_token,
      })

      // Clear storage and reset user state
      storage().removeItem(STORAGE_KEY)
      setSession(undefined)

      // Redirect to login or home page after logging out
      gotoLogin()
    } catch (error) {
      console.error("Logout failed", error)
    }
  }, [])

  const checkSession = async (): Promise<Session | undefined> => {
    // Check if the session is already stored
    const storedSession = storage().getItem(STORAGE_KEY)
    if (storedSession) {
      const parsedSession: Session = JSON.parse(storedSession)

      // Check if the session has expired
      if (dayjs().isAfter(dayjs(parsedSession.expires_at))) {
        storage().removeItem(STORAGE_KEY)
        setSession(undefined)
        setMetadataPublic(DEFAULT_METADATA)
        return undefined
      }

      setSession(parsedSession)
      setMetadataPublic(
        (parsedSession.identity?.metadata_public as
          | UserMetadataPublic
          | undefined) ?? DEFAULT_METADATA,
      )
      return parsedSession
    }

    // If no stored session, check session via Ory SDK
    try {
      const sessionResponse = await orySDK.toSession()

      if (sessionResponse.status === 200) {
        const { data: activeSession } = sessionResponse
        setSession(activeSession)
        setMetadataPublic(
          (activeSession.identity?.metadata_public as
            | UserMetadataPublic
            | undefined) ?? DEFAULT_METADATA,
        )
        storage().setItem(STORAGE_KEY, JSON.stringify(activeSession))
        return activeSession
      } else {
        return undefined
      }
    } catch (error) {
      console.error("Session check failed", error)
      gotoLogin()
      return undefined
    }
  }

  return (
    <AuthContext.Provider
      value={{
        checkSession,
        isAuthenticated: !!session,
        logout,
        metadataPublic,
        session,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
